var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { identity } from 'purify-ts';
import * as apiModels from '@codegrade/api-models';
import { Just, Maybe, Nothing } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { array, assert, expr, itertools, maybe, object } from '@codegrade/utils';
import { NONEXISTENT } from '@/constants';
import { CoursePermissions as CPerm } from '@/permissions';
import { store, SubmissionsStore } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { ValidationError } from './errors';
const defaultDescriptionType = 'markdown';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricItem {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        return new RubricItem({
            id: data.id,
            points: data.points,
            header: data.header,
            description: maybe.wrapMaybeNullable(data.description).orDefault(''),
        });
    }
    /**
     * Create a rubric rubric item from community library item data.
     *
     * @param data The data to use.
     * @returns The created rubric item.
     */
    static fromCommunityLibraryData(data) {
        return new RubricItem({
            id: undefined,
            points: data.points.valueOf(),
            header: data.header,
            description: maybe.wrapMaybeNullable(data.description).orDefault(''),
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty() {
        return new RubricItem({
            id: undefined,
            points: '',
            header: '',
            description: '',
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    duplicate() {
        const points = this.points;
        return new RubricItem({
            id: undefined,
            points,
            header: this.header,
            description: this.description,
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(item, trackingId) {
        this.trackingId = trackingId;
        this.id = item.id;
        this.points = item.points;
        this.header = item.header;
        this.description = item.description;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(props = {}) {
        return new RubricItem({
            ...this,
            ...utils.pickKeys(props, ['id', 'points', 'header', 'description'], false),
        }, this.trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get nonEmptyHeader() {
        return this.header || '[No name]';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        if (!(other instanceof RubricItem)) {
            return false;
        }
        return (this.points === other.points &&
            this.header === other.header &&
            this.description === other.description);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricRowValidationError extends ValidationError {
    /** The invalid categories. */
    categories = [];
    /** The errors with the minimums of continuous rows. */
    continuousMin = [];
    /** The errors with the maximums of continuous rows. */
    continuousMax = [];
    /** The errors when both max and min are zero of continuous rows. */
    continuousZero = [];
    /** The issues with headers of items. */
    itemHeader = [];
    /** The issues with points of items. */
    itemPoints = [];
    /** The unnamed rows. */
    unnamed = false;
    /** The the maximum points. */
    maxPoints = false;
    /** Create new instance. */
    constructor() {
        super();
        Object.seal(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get hasErrors() {
        return (this.categories.length > 0 ||
            this.continuousMin.length > 0 ||
            this.continuousMax.length > 0 ||
            this.continuousZero.length > 0 ||
            this.itemHeader.length > 0 ||
            this.itemPoints.length > 0 ||
            this.unnamed ||
            this.maxPoints);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricRow {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(row, trackingId) {
        switch (row.type) {
            case 'continuous':
            case 'normal':
            case '':
                break;
            default:
                assert.AssertionError.typeAssert(row.type);
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new ReferenceError(`Could not find specified type: ${row.type}`);
        }
        this.trackingId = trackingId ?? utils.getUniqueId();
        this.id = row.id;
        this.type = row.type;
        this.header = row.header;
        this.description = row.description;
        this.descriptionType = row.descriptionType;
        this.locked = row.locked;
        this.items = Object.freeze(row.items.map(item => item));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, opts) {
        switch (data.type) {
            case 'normal':
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                return NormalRubricRow.fromApiServerData(data, opts);
            case 'continuous':
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                return ContinuousRubricRow.fromApiServerData(data, opts);
            default:
                // @ts-expect-error The type of a rubric row is typed as a string
                return assert.AssertionError.assertNever(data);
        }
    }
    /**
     * Create a rubric row from community library item data.
     *
     * @param data The data to use.
     * @returns The created rubric row.
     */
    static fromCommunityLibraryData(data) {
        return expr.match(data, 'tag', {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            discrete: NormalRubricRow.fromCommunityLibraryData,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            continuous: ContinuousRubricRow.fromCommunityLibraryData,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        const apiServerData = apiModels.RubricRowBase.decode(data)
            .mapLeft((errValue) => new Error(errValue))
            .unsafeCoerce();
        return RubricRow.fromApiServerData(apiServerData);
    }
    /**
     * Update this model with data received from the backend.
     *
     * @param data The updated backend model.
     * @returns The updated rubric row.
     */
    updateFromApiServerData(data) {
        const row = {
            description: data.description.orDefault(''),
            items: data.items.map(RubricItem.fromServerData),
            ...utils.pickKeys(data, ['id', 'locked', 'type', 'header', 'descriptionType']),
        };
        return new this.constructor(row, this.trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty() {
        return new RubricRow({
            id: undefined,
            type: '',
            header: '',
            description: '',
            descriptionType: defaultDescriptionType,
            locked: false,
            items: [],
        });
    }
    /** @returns A copy of this rubric row. */
    duplicate() {
        let newHeader = this.header;
        if (newHeader) {
            newHeader += ' (duplicate)';
        }
        return new this.constructor({
            id: undefined,
            type: this.type,
            header: newHeader,
            description: this.description,
            // New rows always have the description type markdown, as we
            // cannot create plain text rows on the server.
            descriptionType: 'markdown',
            locked: false,
            items: this.items.map(item => item.duplicate()),
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    /** Is the description in markdown. */
    get isMarkdown() {
        return this.descriptionType === 'markdown';
    }
    get minPoints() {
        const found = this.items.reduce((minFound, item) => {
            const pts = item.points;
            if (pts != null && pts !== '') {
                return Math.min(minFound, pts);
            }
            return minFound;
        }, Infinity);
        return found === Infinity ? 0 : found;
    }
    /** The maximum number of points that can be achieved in this rubric row. */
    get maxPoints() {
        const maxPoints = Math.max(...object.filterMap(this.items, (item) => {
            const pts = item.points;
            if (pts == null || pts === '') {
                return Nothing;
            }
            else {
                return new Just(pts);
            }
        }));
        if (maxPoints === -Infinity) {
            return 0;
        }
        return maxPoints;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(props = {}) {
        return new this.constructor({
            ...this,
            ...utils.pickKeys(props, ['id', 'type', 'header', 'description', 'descriptionType', 'locked', 'items'], false),
        }, this.trackingId);
    }
    /**
     * Update the header for this rubric row.
     *
     * @param this The rubric row to update.
     * @param header The new header we wish to set.
     * @returns The updated rubric row.
     */
    updateHeader(header) {
        return this.update({ header });
    }
    /**
     * Update the description for this rubric row.
     *
     * @param this The rubric row to update.
     * @param description The new description we wish to set.
     * @returns The updated rubric row.
     */
    updateDescription(description) {
        return this.update({ description });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateItem(idx, prop, value) {
        const items = this.items.slice();
        if (idx < 0 || idx >= items.length) {
            throw new ReferenceError('Invalid index');
        }
        if (prop === 'points') {
            const pts = utils.parseOrKeepFloat(value);
            items[idx] = items[idx].update({
                points: Number.isNaN(pts) ? null : pts,
            });
        }
        else {
            items[idx] = items[idx].update({ [prop]: value });
        }
        return this.update({ items });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    createItem() {
        return this.update({
            items: [...this.items, RubricItem.createEmpty()],
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteItem(idx) {
        if (idx < 0 || idx >= this.items.length) {
            throw new ReferenceError('Invalid index');
        }
        return this.update({
            items: this.items.slice(0, idx).concat(this.items.slice(idx + 1)),
        });
    }
    /**
     * Get a message why the row is locked.
     *
     * @param autoTest The AutoTest of the assignment, or `null` if there is none.
     * @param autoTestResult The AutoTestResult the user is looking at right now.
     * @param rubricResult The result for the current user.
     * @returns A message explaining why the current row is locked.
     */
    lockMessage(autoTest, autoTestResult, rubricResult) {
        switch (this.locked) {
            case 'auto_test':
                return this._autoTestLockMessage(autoTest, autoTestResult, rubricResult);
            case 'auto_test_2':
                return 'This is an AutoTest category.';
            case false:
                return '';
            default:
                return assert.AssertionError.assertNever(this.locked);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _autoTestLockMessage(_, autoTestResult, rubricResult) {
        return maybe
            .fromNullable(this.id)
            .chain(id => maybe.and(maybe.fromNullable(rubricResult?.selected?.[id]), maybe.fromNullable(autoTestResult?.rubricResults?.[id]?.percentage)))
            .caseOf({
            Just: ([selectedRubricItem, autoTestPercentage]) => {
                const amountPoints = utils.toMaxNDecimals(selectedRubricItem.points * selectedRubricItem.multiplier, 2);
                return `You scored ${autoTestPercentage.toFixed(0)}% in the corresponding AutoTest category, which scores you ${amountPoints} points in this rubric category. `;
            },
            Nothing: () => {
                let msg = 'This is an AutoTest category.';
                if (autoTestResult != null) {
                    const extra = [];
                    if (!autoTestResult.finished) {
                        extra.push('the test has finished running');
                    }
                    if (autoTestResult.isFinal.mapOrDefault(utils.not, false)) {
                        extra.push('all hidden steps have been run, which will happen after the deadline');
                    }
                    if (extra.length === 0) {
                        extra.push('you have the permission to see the final grade');
                    }
                    msg += ` It will be filled in once ${extra.join(' and ')}.`;
                }
                return msg;
            },
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get nonEmptyHeader() {
        return this.header || '[No name]';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    validate(prevErrors = null) {
        let errors;
        if (prevErrors == null) {
            errors = new RubricRowValidationError();
        }
        else {
            assert.AssertionError.assert(prevErrors instanceof RubricRowValidationError, 'prevErrors should be an instance of RubricRowValidationError');
            errors = prevErrors;
        }
        if (this.header.length === 0) {
            errors.unnamed = true;
        }
        if (this.items.length === 0) {
            errors.categories.push(this.nonEmptyHeader);
        }
        for (let j = 0; j < this.items.length; j += 1) {
            const item = this.items[j];
            if (item.header.length === 0) {
                errors.itemHeader.push(this.nonEmptyHeader);
            }
            if (item.points == null || Number.isNaN(utils.parseOrKeepFloat(item.points))) {
                errors.itemPoints.push(`'${this.nonEmptyHeader} - ${item.nonEmptyHeader}'`);
            }
        }
        return errors;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        if (!(other instanceof this.constructor)) {
            return false;
        }
        if (this.items.length !== other.items.length) {
            return false;
        }
        return (this.type === other.type &&
            this.header === other.header &&
            this.description === other.description &&
            this.descriptionType === other.descriptionType &&
            this.locked === other.locked &&
            this.items.every((item, i) => item.equals(other.items[i])));
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], RubricRow.prototype, "minPoints", null);
__decorate([
    CacheDecorator.cache
], RubricRow.prototype, "maxPoints", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class NormalRubricRow extends RubricRow {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static tag = 'normal';
    /** The row type to display to users. */
    static displayType = 'discrete';
    /** The row type to display to users. */
    get displayType() {
        return NormalRubricRow.displayType;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, opts) {
        if (data.type !== 'normal') {
            throw new TypeError('Row is not normal');
        }
        return new NormalRubricRow({
            id: data.id,
            type: data.type,
            header: data.header,
            description: data.description.orDefault(''),
            descriptionType: data.descriptionType,
            locked: data.locked,
            items: data.items.map(item => RubricItem.fromServerData(item)),
        }, opts?.trackingId);
    }
    /**
     * Create a normal rubric row from community library item data.
     *
     * @param data The data to create the row from.
     * @returns A fresh normal rubric row.
     */
    static fromCommunityLibraryData(data) {
        return new NormalRubricRow({
            id: undefined,
            type: 'normal',
            header: data.header,
            description: data.description.orDefault(''),
            descriptionType: data.descriptionType,
            locked: false,
            items: data.items.map(item => RubricItem.fromCommunityLibraryData(item)),
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty() {
        return new NormalRubricRow({
            id: undefined,
            type: 'normal',
            header: '',
            description: '',
            descriptionType: defaultDescriptionType,
            locked: false,
            items: [],
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _autoTestLockMessage(nullableAutoTest, autoTestResult, rubricResult) {
        const baseMessage = super._autoTestLockMessage(nullableAutoTest, autoTestResult, rubricResult);
        return Maybe.fromNullable(nullableAutoTest)
            .map(autoTest => autoTest.gradeCalculation.mapOrDefault(gradeCalculation => {
            const toReach = gradeCalculation === 'full' ? 'upper' : 'lower';
            return `You need to reach the ${toReach} bound of a rubric item to achieve its score.`;
        }, 'No grade calculation method has been set yet.'))
            .mapOrDefault(extraMsg => `${baseMessage} ${extraMsg}`, baseMessage);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getTemplateContinuousRow() {
        const allPoints = object.filterMap(this.items, item => {
            const points = item.points;
            if (typeof points === 'number') {
                return Maybe.of(points);
            }
            return Maybe.empty();
        });
        const highest = array.findMax(identity, allPoints).orDefault(0);
        let lowest = array.findMin(identity, allPoints).orDefault(0);
        if (highest === lowest) {
            lowest -= 1;
        }
        lowest = Math.min(0, lowest);
        const items = [
            RubricItem.createEmpty().update({
                header: 'Min points',
                points: lowest,
            }),
            RubricItem.createEmpty().update({
                header: 'Max points',
                points: highest,
            }),
        ];
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return new ContinuousRubricRow({
            id: undefined,
            header: this.header,
            description: this.description,
            descriptionType: this.descriptionType,
            locked: this.locked,
            type: 'continuous',
            items,
        });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ContinuousRubricRow extends RubricRow {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static tag = 'continuous';
    /** The row type to display to users. */
    static displayType = 'continuous';
    /** The row type to display to users. */
    get displayType() {
        return ContinuousRubricRow.displayType;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, opts) {
        if (data.type !== 'continuous') {
            throw new TypeError('Row is not continuous');
        }
        // Continuous rubric rows used to contain only a single item with the
        // maximum achievable points. Now they contain two items, we add an
        // item with a lower bound of 0 points if there is no such item.
        const items = data.items.map(RubricItem.fromServerData);
        if (items.length === 1) {
            const minItem = RubricItem.createEmpty().update({
                header: 'Min points',
                points: 0,
            });
            items.unshift(minItem);
        }
        return new ContinuousRubricRow({
            id: data.id,
            type: data.type,
            header: data.header,
            description: data.description.orDefault(''),
            descriptionType: data.descriptionType,
            locked: data.locked,
            items,
        }, opts?.trackingId);
    }
    /**
     * Create a continuous rubric row from community library item data.
     *
     * @param data The data to create the row from.
     * @returns A fresh continuous rubric row.
     */
    static fromCommunityLibraryData(data) {
        const items = [
            RubricItem.fromCommunityLibraryData({
                points: data.minPoints,
                header: data.header,
                description: '',
            }),
            RubricItem.fromCommunityLibraryData({
                points: data.maxPoints,
                header: data.header,
                description: '',
            }),
        ];
        return new ContinuousRubricRow({
            id: undefined,
            type: 'continuous',
            header: data.header,
            description: data.description.orDefault(''),
            descriptionType: data.descriptionType,
            locked: false,
            items,
        }, utils.getUniqueId());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty() {
        return new ContinuousRubricRow({
            id: undefined,
            type: 'continuous',
            header: '',
            description: '',
            descriptionType: defaultDescriptionType,
            locked: false,
            items: [
                RubricItem.createEmpty().update({ header: 'Min points' }),
                RubricItem.createEmpty().update({ header: 'Max points' }),
            ],
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    validate(prevErrors = null) {
        const errors = super.validate(prevErrors);
        const minPoints = this.items[0].points;
        const maxPoints = this.items[1].points;
        if (typeof minPoints === 'number' && minPoints > 0) {
            errors.continuousMin.push(this.nonEmptyHeader);
        }
        if (typeof maxPoints === 'number' && maxPoints < 0) {
            errors.continuousMax.push(this.nonEmptyHeader);
        }
        if (minPoints === 0 && maxPoints === 0) {
            errors.continuousZero.push(this.nonEmptyHeader);
        }
        return errors;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getTemplateNormalRow() {
        let newItems;
        const minPoints = this.items[0].points;
        const maxPoints = this.items[1].points;
        if (typeof minPoints === 'number' &&
            minPoints < 0 &&
            typeof maxPoints === 'number' &&
            maxPoints > 0) {
            const midPoint = RubricItem.createEmpty().update({
                header: 'Zero points',
                points: 0,
            });
            newItems = [this.items[0], midPoint, this.items[1]];
        }
        else {
            newItems = [...this.items];
        }
        return new NormalRubricRow({
            id: undefined,
            header: this.header,
            description: this.description,
            descriptionType: this.descriptionType,
            // NOTE: We override the locked behavior here to always be NOT locked, since the only use
            // case for this function requires us to be able to edit it after.
            locked: false,
            type: 'normal',
            items: newItems.map(item => RubricItem.createEmpty().update({
                points: item.points,
                header: item.header,
                description: item.description,
            })),
        });
    }
}
const RubricRowsTypes = {
    [NormalRubricRow.tag]: NormalRubricRow,
    [ContinuousRubricRow.tag]: ContinuousRubricRow,
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Rubric {
    rows;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        return new Rubric((data ?? []).map(RubricRow.fromServerData));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        return new Rubric(data.map(row => RubricRow.fromApiServerData(row)));
    }
    /**
     * Create a rubric from community library item data.
     *
     * @param data The data to use.
     * @returns The created rubric.
     */
    static fromCommunityLibraryData(data) {
        return new Rubric(data.map(row => RubricRow.fromCommunityLibraryData(row)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFromApiServerData(data) {
        // A newly created row has no id yet, but we need to know the id to be
        // able to update a row. Unfortunately it is not possible to simply
        // overwrite the old rubric in the store with an entirely new one
        // created from the data sent back by the server, because then the
        // tracking ids of the rubric rows will change, defeating the point of
        // the tracking ids.
        const rows = Array.from(itertools.zip(this.rows, data), ([r, n]) => r.updateFromApiServerData(n));
        return new Rubric(rows);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(rows) {
        this.rows = rows;
        this.rows = Object.freeze(rows);
        Object.freeze(this);
    }
    get maxPoints() {
        return array.sumMap(this.rows, object.attr('maxPoints'));
    }
    get rowsById() {
        return this.rows.reduce((acc, row) => {
            if (row.id != null) {
                acc[row.id] = row;
            }
            return acc;
        }, {});
    }
    get manuallyMarkedRows() {
        return this.rows.filter(row => row.locked === false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getItemIds() {
        return this.rows.reduce((acc, row) => {
            row.items.forEach(item => {
                if (typeof item.id === 'number') {
                    acc[item.id] = `${row.nonEmptyHeader} - ${item.nonEmptyHeader}`;
                }
            });
            return acc;
        }, {});
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    createRow(type) {
        // TODO: Make 'type' argument non-nullable once RubricEditorV1 is
        // removed.
        let newRow;
        if (type == null) {
            newRow = RubricRow.createEmpty();
        }
        else {
            const cls = RubricRowsTypes[type];
            if (cls == null) {
                throw new TypeError(`Invalid row type: ${type}`);
            }
            else {
                newRow = cls.createEmpty();
            }
        }
        const rows = [...this.rows, newRow];
        return new Rubric(rows);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    duplicateRow(idx) {
        // Duplicate the row at the given index and insert it right after the
        // given index.
        const rows = this.rows;
        const newRow = this.rows[idx].duplicate();
        const newRows = rows.slice(0, idx + 1);
        newRows.push(newRow, ...rows.slice(idx + 1));
        return new Rubric(newRows);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteRow(idx) {
        if (idx < 0 || idx >= this.rows.length) {
            throw new ReferenceError('Invalid index');
        }
        const rows = this.rows.slice(0, idx).concat(this.rows.slice(idx + 1));
        return new Rubric(rows);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateRow(idx, rowData) {
        if (!(rowData instanceof RubricRow)) {
            throw new TypeError('Rubric rows must be of type RubricRow');
        }
        const rows = this.rows.slice();
        rows[idx] = rowData;
        return new Rubric(rows);
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    setRows(rows) {
        return new Rubric(rows);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    equals(other) {
        if (!(other instanceof Rubric)) {
            return false;
        }
        if (this.rows.length !== other.rows.length) {
            return false;
        }
        return this.rows.every((row, i) => row.equals(other.rows[i]));
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Rubric.prototype, "maxPoints", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Rubric.prototype, "rowsById", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Rubric.prototype, "manuallyMarkedRows", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricResultValidationError extends ValidationError {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    multipliers;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor() {
        super();
        this.multipliers = [];
        Object.seal(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get hasErrors() {
        return this.multipliers.length > 0;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricResultItem extends apiModels.WorkRubricItem {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(data) {
        super(data);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        return new RubricResultItem(data);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromRubricItem(item, multiplier) {
        const achievedPoints = multiplier * item.points;
        return new RubricResultItem({ ...item, multiplier, achievedPoints });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RubricResult {
    submissionId;
    selected;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(submissionId, data) {
        const apiServerData = apiModels.WorkRubricResultAsJSON.decode(data)
            .mapLeft((errValue) => new Error(errValue))
            .unsafeCoerce();
        return RubricResult.fromApiServerData(submissionId, apiServerData);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(submissionId, data) {
        const rowOfItem = data.rubrics.reduce((acc, row) => {
            row.items.forEach(item => {
                acc[item.id] = row.id;
            });
            return acc;
        }, {});
        const selected = data.selected.reduce((acc, item) => {
            const rowId = rowOfItem[item.id];
            acc[rowId] = RubricResultItem.fromApiServerData(item);
            return acc;
        }, {});
        return new RubricResult(submissionId, selected);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(submissionId, selected) {
        this.submissionId = submissionId;
        this.selected = selected;
        this.submissionId = submissionId;
        this.selected = Object.freeze(selected);
        Object.freeze(this);
    }
    get points() {
        if (utils.isEmpty(this.selected)) {
            return null;
        }
        else {
            return array.sumMap(Object.values(this.selected), item => {
                if (typeof item.multiplier !== 'number') {
                    return 0;
                }
                else {
                    return item.points * Math.max(0, Math.min(1, item.multiplier));
                }
            });
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get submission() {
        // TODO: Check where we use this and make it work with Maybe<Submission>
        return SubmissionsStore.getSingleSubmission()(this.submissionId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get assignment() {
        return this.submission.chainNullable(object.attr('assignment'));
    }
    /** The rubric of this result, if it can be found. */
    get maybeRubric() {
        return this.submission.chainNullable(sub => store.getters['rubrics/rubrics'][sub.assignmentId]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get rubric() {
        return this.maybeRubric.extractNullable();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get maxPoints() {
        return Maybe.fromNullable(this.assignment.map(object.attr('fixed_max_rubric_points')).extract()).alt(Maybe.fromNullable(this.rubric?.maxPoints));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get grade() {
        return maybe
            .and(this.numericGrade, this.assignment)
            .map(([g, assignment]) => assignment.formatUnscaledGrade(g));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get numericGrade() {
        const maxGrade = this.assignment.mapOrDefault(assignment => assignment.max_grade, 10);
        return utils.maybeUtils
            .and(Maybe.fromNullable(this.points), this.maxPoints)
            .map(([points, maxPoints]) => {
            const grade = (10 * points) / maxPoints;
            return Math.max(0, Math.min(maxGrade, grade));
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getMultiplierForRow(row) {
        const item = this.selected[row.id];
        if (item == null) {
            return Nothing;
        }
        switch (row.type) {
            case 'normal':
                return Maybe.of(item.multiplier);
            case 'continuous': {
                const { points, multiplier } = item;
                const { minPoints, maxPoints } = row;
                // maxPoints is strictly greater than minPoints so we will never
                // divide by 0 here.
                return Maybe.of((100 * (points * multiplier - minPoints)) / (maxPoints - minPoints));
            }
            case '':
                return assert.AssertionError.assert(false, 'Expected the rubric row to have a type.');
            default:
                return assert.AssertionError.assertNever(row.type);
        }
    }
    get scorePerRow() {
        return Maybe.fromNullable(this.rubric?.rows).mapOrDefault(rows => rows.reduce((acc, row) => {
            const item = this.selected[row.id];
            if (item != null) {
                acc[row.id] = item.points * item.multiplier;
            }
            return acc;
        }, {}), {});
    }
    get manuallyMarkedRows() {
        const { rubric } = this;
        if (rubric == null) {
            return [];
        }
        const manualRows = object.makeLookupMapping(rubric.manuallyMarkedRows, 'id');
        return Object.keys(this.selected).filter(key => object.hasAttr(manualRows, key));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isChangedComparedTo(other) {
        const lockedRows = new Set(this.rubric?.rows.filter(row => row.locked !== false).map(r => `${r.id}`));
        for (const [key, value] of Object.entries(this.selected)) {
            if (lockedRows.has(key)) {
                continue;
            }
            const otherValue = other.selected[key];
            if (otherValue == null) {
                return true;
            }
            if (otherValue.id !== value.id || otherValue.multiplier !== value.multiplier) {
                return true;
            }
        }
        // This means that an item of a non locked rubric row is present in the
        // other result, but not in this result.
        return Object.keys(other.selected).some(otherId => !lockedRows.has(otherId) && !object.hasAttr(this.selected, otherId));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    toggleItem(rowId, item) {
        const selected = { ...this.selected };
        const selectedItem = selected[rowId];
        if (selectedItem != null && selectedItem.id === item.id) {
            delete selected[rowId];
        }
        else {
            selected[rowId] = RubricResultItem.fromRubricItem(item, 1);
        }
        return new RubricResult(this.submissionId, selected);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    validate() {
        if (this.rubric == null) {
            throw new ReferenceError('Rubric not found.');
        }
        const errors = new RubricResultValidationError();
        const rows = this.rubric.rowsById;
        Object.entries(this.selected).forEach(([rowId, item]) => {
            const row = rows[parseInt(rowId, 10)];
            if (item.multiplier != null && (item.multiplier < 0 || item.multiplier > 1)) {
                errors.multipliers.push(row.nonEmptyHeader);
            }
        });
        return errors;
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    clearSelected() {
        return new RubricResult(this.submissionId, {});
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], RubricResult.prototype, "points", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], RubricResult.prototype, "scorePerRow", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], RubricResult.prototype, "manuallyMarkedRows", null);
/**
 * Get the rubric row getters used by the editor by loading the data from the store.
 *
 * @param assignment The assignment for which you should get the rows.
 * @returns An object to retrieve a single row, and all rows.
 */
export function getStoreRubricRowsGetter(assignment) {
    let didLoad = false;
    /**
     * Get the rubric of the given assignment.
     *
     * @returns The rubric of this assignment. If the outer `Maybe` is `Nothing`,
     * that means the rubric hasn't loaded yet, and we should not try to submit
     * a rubric, because we might overwrite the rubric on the server.
     */
    const getRubric = () => Maybe.fromNullable(store.getters['rubrics/rubrics'][assignment.id])
        .map((rubric) => rubric === NONEXISTENT ? Maybe.empty() : Maybe.of(rubric))
        .ifNothing(async () => {
        if (didLoad) {
            return;
        }
        try {
            await store.dispatch('rubrics/loadRubric', { assignmentId: assignment.id }, { root: true });
        }
        catch (caught) {
            // We catch any error here, because we can't display it anyway.
            // eslint-disable-next-line no-console
            console.error(caught);
        }
        didLoad = true;
    });
    /** @returns Maybe a non empty list of all rows in the store. */
    const getRows = () => getRubric()
        .join()
        .chain(rubric => NonEmptyList.fromArray([...rubric.rows]));
    const submitRubricRow = Maybe.fromPredicate(() => assignment.hasPermission(CPerm.manage_rubrics), async (newRow) => {
        // Load the rubric if we haven't loaded it yet, so we don't accidentally
        // overwrite an existing rubric.
        const maybeRubric = await getRubric().caseOf({
            Nothing: async () => {
                await store.dispatch('rubrics/loadRubric', { assignmentId: assignment.id }, { root: true });
                return getRubric().join();
            },
            Just: async (x) => x,
        });
        const rubric = maybeRubric
            .map((r) => r.setRows([...r.rows, newRow]))
            .orDefaultLazy(() => new Rubric([newRow]));
        const response = await store.dispatch('rubrics/updateRubric', { assignmentId: assignment.id, rubric }, { root: true });
        return response.map(rows => rows[rows.length - 1]);
    });
    return Object.freeze({
        getRows,
        getRow: (rubricRowId) => getRows().chain(rows => array.findMaybe(row => row.id === rubricRowId, rows)),
        submitRubricRow,
    });
}
